import { css } from "lit";

/** Shared layout primitives reused across portal pages.
 *
 * Mobile-first per the design system (§6): base styles target Compact
 * (<600px, 16px container padding); Medium (≥600px) and Expanded (≥840px)
 * enhance progressively. Spacing comes from the --space-* scale
 * (tokens/spacing.css); breakpoints are the M3 ones — 600/840/1200/1600. */
export const sharedStyles = css`
  :host {
    display: block;
  }

  .page {
    max-width: 960px;
    margin: 0 auto;
    padding: var(--space-4, 16px);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--space-4, 16px);
  }

  @media (min-width: 600px) {
    .page {
      padding: var(--space-6, 24px);
      gap: var(--space-6, 24px);
    }
  }

  .page-header {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  .page-header > div:first-child,
  .page-header h1 {
    flex: 1;
    min-width: min(220px, 100%);
  }

  .page-header h1 {
    margin: 0;
    /* Fluid between m3-headline-small (24px) and m3-headline-medium (28px). */
    font: 400 clamp(24px, 3.5vw + 12px, 28px) / 1.2
      var(--font-brand, Roboto, sans-serif);
    overflow-wrap: break-word;
  }

  .page-header p {
    margin: 4px 0 0;
    color: var(--md-sys-color-on-surface-variant);
    font-size: 0.8125rem;
  }

  /* Stat tiles (dashboard / NFC overview). */
  .stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 14px;
  }

  .stat {
    background: var(--md-sys-color-surface-container-low);
    border-radius: 12px;
    padding: 16px;
  }

  .stat .n {
    font: 400 32px/1.1 var(--font-brand, Roboto, sans-serif);
  }

  .stat .l {
    font: 500 12px/1.3 var(--font-plain, sans-serif);
    color: var(--md-sys-color-on-surface-variant);
    margin-top: 4px;
  }

  .card {
    background: var(--md-sys-color-surface);
    border: 1px solid var(--md-sys-color-outline-variant);
    border-radius: 16px;
    padding: 20px;
  }

  .card--flush {
    padding: 0;
    overflow: hidden;
  }

  .card h2 {
    margin: 0 0 12px;
    font-size: 1.125rem;
    font-weight: 600;
  }

  /* Card header row for flush (table) cards. */
  .cardhead {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    flex-wrap: wrap;
  }

  .cardhead h2 {
    margin: 0;
    font: 500 16px/1.2 var(--font-plain, sans-serif);
    flex: 1;
    min-width: 160px;
  }

  .cardhead h2 small {
    font: 400 13px/1 var(--font-plain, sans-serif);
    color: var(--md-sys-color-on-surface-variant);
  }

  .form-grid {
    display: grid;
    gap: 16px;
  }

  .form-row {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .form-row > * {
    flex: 1;
    min-width: 200px;
  }

  /* Wide-data mobile pattern (design system §6: tables get a horizontal
     scroll container on Compact). The container owns the overflow so the
     page never scrolls sideways; edge shadows appear only while there is
     more to scroll (background-attachment affordance). */
  .table-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    background:
      linear-gradient(to right, var(--md-sys-color-surface) 30%, transparent),
      linear-gradient(to left, var(--md-sys-color-surface) 30%, transparent)
        100% 0,
      radial-gradient(
        farthest-side at 0 50%,
        color-mix(in srgb, var(--md-sys-color-shadow, #000) 18%, transparent),
        transparent
      ),
      radial-gradient(
          farthest-side at 100% 50%,
          color-mix(in srgb, var(--md-sys-color-shadow, #000) 18%, transparent),
          transparent
        )
        100% 0;
    background-repeat: no-repeat;
    background-size:
      40px 100%,
      40px 100%,
      14px 100%,
      14px 100%;
    background-attachment: local, local, scroll, scroll;
  }

  .table-scroll table {
    /* Columns keep a readable width; the container scrolls instead. */
    min-width: 560px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font: 400 14px/1.4 var(--font-plain, sans-serif);
  }

  th,
  td {
    text-align: left;
    vertical-align: middle;
  }

  th {
    font: 500 12px/1.2 var(--font-plain, sans-serif);
    color: var(--md-sys-color-on-surface-variant);
    padding: var(--space-2, 8px) var(--space-3, 12px);
    border-bottom: 1px solid var(--md-sys-color-outline-variant);
  }

  td {
    padding: var(--space-3, 12px);
    border-bottom: 1px solid var(--md-sys-color-outline-variant);
  }

  @media (min-width: 840px) {
    th {
      padding: 10px var(--space-5, 20px);
    }

    td {
      padding: var(--space-3, 12px) var(--space-5, 20px);
    }
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover td {
    background: color-mix(
      in srgb,
      var(--md-sys-color-on-surface) 4%,
      transparent
    );
  }

  td small,
  .td-sub {
    display: block;
    color: var(--md-sys-color-on-surface-variant);
    font-size: 12px;
  }

  /* Status / GI badge vocabulary: color is always paired with an icon + text. */
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 26px;
    padding: 0 10px;
    border-radius: 999px;
    font: 500 12px/1 var(--font-plain, sans-serif);
    white-space: nowrap;
  }

  .badge md-icon {
    --md-icon-size: 15px;
  }

  .badge--ok {
    background: var(--md-sys-color-primary-container);
    color: var(--md-sys-color-on-primary-container);
  }

  .badge--neutral {
    background: var(--md-sys-color-surface-container-highest);
    color: var(--md-sys-color-on-surface-variant);
  }

  .badge--bad {
    background: var(--md-sys-color-error-container);
    color: var(--md-sys-color-on-error-container);
  }

  .badge--gi {
    background: var(--md-sys-color-tertiary-container);
    color: var(--md-sys-color-on-tertiary-container);
  }

  /* Reconciliation bar + legend (token-built, no chart lib). */
  .recon-bar {
    display: flex;
    height: 14px;
    border-radius: 999px;
    overflow: hidden;
    background: var(--md-sys-color-surface-container-high);
  }

  .recon-bar span {
    height: 100%;
  }

  .legend {
    display: flex;
    gap: 18px;
    margin-top: 12px;
    font: 400 12px/1.2 var(--font-plain, sans-serif);
    color: var(--md-sys-color-on-surface-variant);
    flex-wrap: wrap;
  }

  .legend i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 3px;
    margin-right: 6px;
  }

  .mono {
    font-family: var(--font-mono, ui-monospace, monospace);
    font-size: 0.8125rem;
  }

  .empty {
    color: var(--md-sys-color-on-surface-variant);
    font-style: italic;
    padding: 32px 16px;
    text-align: center;
  }

  .error-text {
    color: var(--md-sys-color-error);
    font-size: 0.875rem;
  }

  .muted {
    color: var(--md-sys-color-on-surface-variant);
  }

  .actions-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
  }

  .center-loading {
    display: flex;
    justify-content: center;
    padding: 48px 0;
  }

  code {
    font-family: var(--font-mono, ui-monospace, monospace);
    font-size: 13px;
    word-break: break-all;
  }

  /* Rounded search/filter field used in card headers. Fluid: takes the row
     it wraps onto at Compact instead of forcing a 220px column. */
  .cardhead md-outlined-text-field {
    --md-outlined-text-field-container-shape: 999px;
    flex: 1 1 180px;
    min-width: 0;
    max-width: 280px;
  }

  md-icon svg {
    width: 1em;
    height: 1em;
    fill: currentColor;
  }

  /* Dialogs: near-full-width sheets on Compact (design system §6), natural
     M3 sizing from 600px up. */
  md-dialog {
    min-width: min(calc(100vw - var(--space-8, 32px)), 560px);
    max-width: calc(100vw - var(--space-4, 16px));
  }

  @media (min-width: 600px) {
    md-dialog {
      min-width: 360px;
      max-width: 560px;
    }
  }

  /* Visible focus for keyboard users (design system §7). */
  a:focus-visible,
  button:focus-visible,
  [tabindex]:focus-visible {
    outline: 2px solid var(--md-sys-color-primary);
    outline-offset: 2px;
  }

  /* Motion respects the user (design system §7). */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;

/** Centered-card layout for unauthenticated pages (login/signup/onboarding). */
export const authStyles = css`
  .auth-page {
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-4, 16px);
  }

  .auth-card {
    width: 100%;
    max-width: 440px;
    background: var(--md-sys-color-surface-container-low);
    border: 1px solid var(--md-sys-color-outline-variant);
    border-radius: 24px;
    padding: var(--space-6, 24px);
    display: flex;
    flex-direction: column;
    gap: var(--space-4, 16px);
  }

  @media (min-width: 600px) {
    .auth-page {
      padding: var(--space-6, 24px);
    }

    .auth-card {
      padding: var(--space-8, 32px);
    }
  }

  .auth-card h1 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
  }

  .auth-card .subtitle {
    margin: -8px 0 0;
    color: var(--md-sys-color-on-surface-variant);
    font-size: 0.875rem;
  }

  .auth-card form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .auth-card md-outlined-text-field {
    width: 100%;
  }

  .auth-card .footer-link {
    text-align: center;
    font-size: 0.875rem;
    color: var(--md-sys-color-on-surface-variant);
    margin: 0;
  }

  .auth-card .divider-or {
    display: flex;
    align-items: center;
    gap: 12px;
    color: var(--md-sys-color-on-surface-variant);
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .auth-card .divider-or::before,
  .auth-card .divider-or::after {
    content: "";
    flex: 1;
    height: 1px;
    background: var(--md-sys-color-outline-variant);
  }
`;
